import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { Plus, Settings2, Users as UsersIcon, Link as LinkIcon } from 'lucide-react';
import { Switch } from '../ui/switch';

const CampaignManagement = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [title, setTitle] = useState('');
  const [selectedStaff, setSelectedStaff] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [campaignsData, staffData] = await Promise.all([
        api.getCampaigns(),
        api.getStaff()
      ]);
      setCampaigns(campaignsData.campaigns);
      setStaff(staffData.staff.filter(s => s.enabled));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      await api.createCampaign({ title });
      toast.success('Campaign created successfully');
      setShowCreateDialog(false);
      setTitle('');
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggleEnabled = async (campaignId, currentStatus) => {
    try {
      await api.updateCampaign({ campaign_id: campaignId, enabled: !currentStatus });
      toast.success(`Campaign ${!currentStatus ? 'enabled' : 'disabled'}`);
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAssignStaff = async (e) => {
    e.preventDefault();
    try {
      await api.updateCampaign({
        campaign_id: selectedCampaign.id,
        assigned_staff: selectedStaff
      });
      toast.success('Staff assigned successfully');
      setShowAssignDialog(false);
      setSelectedCampaign(null);
      setSelectedStaff([]);
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openAssignDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setSelectedStaff(campaign.assigned_staff || []);
    setShowAssignDialog(true);
  };

  const toggleStaffSelection = (staffId) => {
    setSelectedStaff(prev =>
      prev.includes(staffId)
        ? prev.filter(id => id !== staffId)
        : [...prev, staffId]
    );
  };

  const handleDownloadCSV = async (campaign) => {
    try {
      const response = await api.getCampaignCSVUrl(campaign.id);
      await navigator.clipboard.writeText(response.url);
      toast.success('CSV link copied to clipboard!');
    } catch (error) {
      toast.error(error.message || 'Failed to get CSV link');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900" data-testid="campaign-management-title">Campaign Management</h2>
          <p className="text-slate-600">Create and manage lead campaigns</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-campaign-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Campaign
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Campaigns</CardTitle>
          <CardDescription>Manage campaigns and assign staff</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading campaigns...</div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No campaigns yet</div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="campaigns-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign ID</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Lead Count</TableHead>
                    <TableHead>Assigned Staff</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <TableRow key={campaign.id} data-testid={`campaign-row-${campaign.id}`}>
                      <TableCell className="font-mono text-sm">{campaign.id.substring(0, 8)}...</TableCell>
                      <TableCell className="font-medium">{campaign.title}</TableCell>
                      <TableCell data-testid={`lead-count-${campaign.id}`}>
                        <Badge variant="outline">{campaign.lead_count} leads</Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">
                          {campaign.assigned_staff?.length || 0} staff
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={campaign.enabled ? 'default' : 'secondary'} data-testid={`campaign-status-${campaign.id}`}>
                          {campaign.enabled ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadCSV(campaign)}
                            data-testid={`copy-csv-link-${campaign.id}`}
                          >
                            <LinkIcon className="w-4 h-4 mr-2" /> Copy Link
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleEnabled(campaign.id, campaign.enabled)}
                            data-testid={`toggle-campaign-${campaign.id}`}
                          >
                            {campaign.enabled ? 'Disable' : 'Enable'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAssignDialog(campaign)}
                            data-testid={`assign-staff-${campaign.id}`}
                          >
                            <UsersIcon className="w-4 h-4 mr-2" /> Assign Staff
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Campaign Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-campaign-dialog">
          <DialogHeader>
            <DialogTitle>Create Campaign</DialogTitle>
            <DialogDescription>Create a new lead campaign</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCampaign} className="space-y-4">
            <div>
              <Label htmlFor="title">Campaign Title</Label>
              <Input
                id="title"
                data-testid="campaign-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="submit-create-campaign">
                Create Campaign
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Staff Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent data-testid="assign-staff-dialog">
          <DialogHeader>
            <DialogTitle>Assign Staff</DialogTitle>
            <DialogDescription>
              Select staff members for {selectedCampaign?.title}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssignStaff} className="space-y-4">
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {staff.length === 0 ? (
                <p className="text-sm text-slate-500">No staff available</p>
              ) : (
                staff.map((member) => (
                  <div key={member.id} className="flex items-center space-x-2 p-2 hover:bg-slate-50 rounded">
                    <Checkbox
                      id={member.id}
                      checked={selectedStaff.includes(member.id)}
                      onCheckedChange={() => toggleStaffSelection(member.id)}
                      data-testid={`checkbox-staff-${member.id}`}
                    />
                    <Label htmlFor={member.id} className="flex-1 cursor-pointer">
                      <div>
                        <p className="font-medium">{member.name}</p>
                        <p className="text-xs text-slate-500">{member.email}</p>
                      </div>
                    </Label>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAssignDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="submit-assign-staff">
                Assign ({selectedStaff.length})
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignManagement;